import axios from "axios";

const API_BASE =
  import.meta.env.VITE_API_URL ?? "https://admin.smartica.co.in/api";

/* ──────────────────────────────────────────────────────────────────────────
 * The two endpoints. Everything else reads blogs through this file, so these
 * are the only place a URL lives.
 *
 * Payload shape (Strapi v4):
 *   { data: [ { id, attributes: { title, slug, publishedAt, createdAt,
 *                                 updatedAt, blogDetails, posterImage,
 *                                 vector } } ], meta: { pagination } }
 *
 * Notes on the fields:
 *   - blogDetails is an HTML string — the post body.
 *   - posterImage is already an absolute S3 URL, so it needs no host prefix.
 *   - vector is the category tag; the public site only shows "website" posts.
 *   - There is no separate excerpt or author field, so the card summary is
 *     derived from blogDetails below.
 *
 * Filtering and pagination both happen in Strapi, not the browser: each page
 * load transfers exactly one page of rows however large the archive grows.
 * ────────────────────────────────────────────────────────────────────────── */
export const BLOG_VECTOR = "website";
export const BLOGS_PER_PAGE = 10;

export const BLOG_LIST_URL = (page = 1) =>
  `${API_BASE}/blog-posts` +
  `?sort=publishedAt:desc` +
  `&filters[vector][$eq]=${BLOG_VECTOR}` +
  `&pagination[page]=${page}` +
  `&pagination[pageSize]=${BLOGS_PER_PAGE}`;
export const BLOG_DETAIL_URL = (id) => `${API_BASE}/blog-posts/${id}`;

/**
 * HTML string → plain text.
 *
 * Parsed into a detached document, which does not run scripts or load
 * resources; this is only used to read textContent for card summaries.
 */
const htmlToText = (html) => {
  if (!html || typeof html !== "string") return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body?.textContent ?? "").replace(/\s+/g, " ").trim();
};

/** First ~140 characters of the body, used as the card summary. */
const buildExcerpt = (html, maxLength = 140) => {
  const text = htmlToText(html);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
};

/** Flatten one API row into the shape the UI renders. */
export const normalizeBlog = (row) => {
  const fields = row?.attributes ?? {};

  return {
    id: row?.id,
    slug: fields.slug,
    title: fields.title?.trim() || "Untitled",
    category: fields.vector,
    image: fields.posterImage,
    body: fields.blogDetails ?? "",
    excerpt: buildExcerpt(fields.blogDetails),
    date: fields.publishedAt ?? fields.createdAt,
  };
};

/**
 * One page of "website" posts, newest first.
 *
 * Returns { blogs, pagination } where pagination is Strapi's
 * { page, pageSize, pageCount, total } block.
 */
export const fetchBlogs = async (page = 1) => {
  const { data } = await axios.get(BLOG_LIST_URL(page));
  const rows = Array.isArray(data?.data) ? data.data : [];
  return {
    blogs: rows.map(normalizeBlog),
    pagination: data?.meta?.pagination ?? {
      page,
      pageSize: BLOGS_PER_PAGE,
      pageCount: rows.length > 0 ? page : 0,
      total: rows.length,
    },
  };
};

/** A single post — the second call, fired when a card is opened. */
export const fetchBlog = async (id) => {
  const { data } = await axios.get(BLOG_DETAIL_URL(id));
  return data?.data ? normalizeBlog(data.data) : null;
};

/** Reader-friendly date; returns "" for missing/unparseable values. */
export const formatBlogDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};
